import type { ProjectEquipmentRoutePoint } from "src/domains/shared/api/equipment/equipment.model";
import {
  localEastNorthM,
  offsetLngLat,
  type EastNorth,
  type LngLat,
} from "./equipmentGeometry";

/**
 * 장비 동선 기하 — 지점을 잇는 centripetal Catmull-Rom 곡선 표본과, 그 표본 폴리라인 위의 재생 자세. 좌표는 항상
 * `[경도, 위도, 정표고 | null]`. 곡선은 원래 지점을 그대로 지난다 — 표본에 원래 지점 값을 그대로 넣는다.
 */

/** 동선 표본 한 점 — 높이는 양 끝 지점 높이를 알 때만 구간 비율로 보간한다 */
export type RouteSample = readonly [
  lng: number,
  lat: number,
  altitudeM: number | null
];

export const ROUTE_SPEED_KMH_MIN = 1;
export const ROUTE_SPEED_KMH_MAX = 20;
/** 새 동선의 속도 — 보드 F13 */
export const ROUTE_SPEED_KMH_DEFAULT = 5;
/** 동선은 장비 위치 + 지점 하나 이상 */
export const ROUTE_MIN_POINTS = 2;
/** 동선 지점 상한(장비 위치 포함) — 서버 결정값(백엔드 spec `project-equipment` 「장비 wire 와 봉투」) */
export const ROUTE_MAX_POINTS = 200;

/** 지점 사이 구간마다 나누는 표본 수 — 구간 가운데(＋ 지점)가 표본 하나에 떨어지도록 짝수 */
const ROUTE_SAMPLES_PER_SEGMENT = 16;
const CENTRIPETAL_ALPHA = 0.5;
// 같은 자리 지점이 겹쳐도 매듭 간격이 0 이 되어 나눗셈이 깨지지 않게
const MIN_KNOT_INTERVAL = 1e-9;

const EARTH_RADIUS_M = 6378137;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const FULL_TURN_DEG = 360;
const HALF_TURN_DEG = 180;
const SECONDS_PER_HOUR = 3600;
const METERS_PER_KM = 1000;

const lngLatOf = (point: RouteSample | ProjectEquipmentRoutePoint): LngLat => [
  point[0],
  point[1],
];

const distance2 = (a: EastNorth, b: EastNorth) =>
  Math.hypot(b[0] - a[0], b[1] - a[1]);

const mix = (a: EastNorth, b: EastNorth, wa: number, wb: number): EastNorth => [
  a[0] * wa + b[0] * wb,
  a[1] * wa + b[1] * wb,
];

/** Barry–Goldman 피라미드 — 매듭 t1..t2 사이의 한 점 */
function centripetalPoint(
  [p0, p1, p2, p3]: readonly [EastNorth, EastNorth, EastNorth, EastNorth],
  [t0, t1, t2, t3]: readonly [number, number, number, number],
  t: number
): EastNorth {
  const a1 = mix(p0, p1, (t1 - t) / (t1 - t0), (t - t0) / (t1 - t0));
  const a2 = mix(p1, p2, (t2 - t) / (t2 - t1), (t - t1) / (t2 - t1));
  const a3 = mix(p2, p3, (t3 - t) / (t3 - t2), (t - t2) / (t3 - t2));
  const b1 = mix(a1, a2, (t2 - t) / (t2 - t0), (t - t0) / (t2 - t0));
  const b2 = mix(a2, a3, (t3 - t) / (t3 - t1), (t - t1) / (t3 - t1));
  return mix(b1, b2, (t2 - t) / (t2 - t1), (t - t1) / (t2 - t1));
}

const knotInterval = (a: EastNorth, b: EastNorth) =>
  Math.max(distance2(a, b) ** CENTRIPETAL_ALPHA, MIN_KNOT_INTERVAL);

/**
 * 지점들을 지나는 centripetal Catmull-Rom 곡선의 표본 — 구간마다 `ROUTE_SAMPLES_PER_SEGMENT` 개로 나누고 구간
 * 경계에는 원래 지점을 그대로 둔다(점을 병합하거나 선분으로 바꾸지 않는다). 양 끝 구간의 바깥 조절점은 끝 지점
 * 기준으로 반사해 만든다. 곡선 계산은 첫 지점 기준 동·북 미터에서 한다.
 */
export function routeCurveSamples(
  points: readonly ProjectEquipmentRoutePoint[]
): RouteSample[] {
  if (points.length < ROUTE_MIN_POINTS) return points.map((point) => point);
  const origin = lngLatOf(points[0]);
  const local = points.map((point) => localEastNorthM(origin, lngLatOf(point)));
  const reflect = (edge: EastNorth, inner: EastNorth): EastNorth => [
    2 * edge[0] - inner[0],
    2 * edge[1] - inner[1],
  ];
  const samples: RouteSample[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const p1 = local[index];
    const p2 = local[index + 1];
    const p0 = index > 0 ? local[index - 1] : reflect(p1, p2);
    const p3 = index + 2 < local.length ? local[index + 2] : reflect(p2, p1);
    const t0 = 0;
    const t1 = t0 + knotInterval(p0, p1);
    const t2 = t1 + knotInterval(p1, p2);
    const t3 = t2 + knotInterval(p2, p3);
    const [, , startAltitude] = points[index];
    const [, , endAltitude] = points[index + 1];
    samples.push(points[index]);
    if (distance2(p1, p2) === 0) continue;
    for (let step = 1; step < ROUTE_SAMPLES_PER_SEGMENT; step += 1) {
      const fraction = step / ROUTE_SAMPLES_PER_SEGMENT;
      const [lng, lat] = offsetLngLat(
        origin,
        centripetalPoint([p0, p1, p2, p3], [t0, t1, t2, t3], t1 + (t2 - t1) * fraction)
      );
      const altitude =
        startAltitude === null || endAltitude === null
          ? null
          : startAltitude + (endAltitude - startAltitude) * fraction;
      samples.push([lng, lat, altitude]);
    }
  }
  samples.push(points[points.length - 1]);
  return samples;
}

/** 지점 i 와 i+1 사이 곡선의 가운데 표본 — 수정 중 "＋" 자리 */
export function routeSegmentMidSample(
  samples: readonly RouteSample[],
  points: readonly ProjectEquipmentRoutePoint[],
  segmentIndex: number
): RouteSample {
  const start = samples.indexOf(points[segmentIndex]);
  const end = samples.indexOf(points[segmentIndex + 1], start + 1);
  return samples[Math.floor((start + end) / 2)];
}

export function haversineDistanceM(a: LngLat, b: LngLat): number {
  const latA = a[1] * DEG_TO_RAD;
  const latB = b[1] * DEG_TO_RAD;
  const deltaLat = latB - latA;
  const deltaLng = (b[0] - a[0]) * DEG_TO_RAD;
  const halfChord =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLng / 2) ** 2;
  return (
    2 *
    EARTH_RADIUS_M *
    Math.atan2(Math.sqrt(halfChord), Math.sqrt(1 - halfChord))
  );
}

/** 표본마다 시작점부터의 누적 수평 거리(m) */
export function routeCumulativeLengthsM(
  samples: readonly RouteSample[]
): number[] {
  const lengths: number[] = [];
  let total = 0;
  samples.forEach((sample, index) => {
    if (index > 0)
      total += haversineDistanceM(
        lngLatOf(samples[index - 1]),
        lngLatOf(sample)
      );
    lengths.push(total);
  });
  return lengths;
}

/** 지점들로 만든 곡선의 길이(m) */
export const routeLengthM = (points: readonly ProjectEquipmentRoutePoint[]) =>
  routeCumulativeLengthsM(routeCurveSamples(points)).at(-1) ?? 0;

const normalizeDeg = (deg: number) =>
  ((deg % FULL_TURN_DEG) + FULL_TURN_DEG) % FULL_TURN_DEG;

/** 짧은 쪽으로 도는 각도 보간 — 북 0°, 시계 */
function lerpAngleDeg(a: number, b: number, t: number): number {
  const shortestDelta = normalizeDeg(b - a + HALF_TURN_DEG) - HALF_TURN_DEG;
  return normalizeDeg(a + shortestDelta * t);
}

/** a 에서 b 를 보는 방위 — 북 0°, 시계 */
export function bearingDeg(a: LngLat, b: LngLat): number {
  const latA = a[1] * DEG_TO_RAD;
  const latB = b[1] * DEG_TO_RAD;
  const deltaLng = (b[0] - a[0]) * DEG_TO_RAD;
  const y = Math.sin(deltaLng) * Math.cos(latB);
  const x =
    Math.cos(latA) * Math.sin(latB) -
    Math.sin(latA) * Math.cos(latB) * Math.cos(deltaLng);
  return normalizeDeg(Math.atan2(y, x) * RAD_TO_DEG);
}

/** 속도(km/h) × 경과 시간(초) → 간 거리(m) */
export const simulationDistanceM = (speedKmh: number, elapsedS: number) =>
  (speedKmh * METERS_PER_KM * elapsedS) / SECONDS_PER_HOUR;

/** 거리(m)를 그 속도로 가는 데 걸리는 시간(초) */
export const routeDurationS = (lengthM: number, speedKmh: number) =>
  (lengthM * SECONDS_PER_HOUR) / (speedKmh * METERS_PER_KM);

export interface RoutePose {
  lng: number;
  lat: number;
  /** 정표고 — 양 끝 표본 높이를 알 때만 */
  altitudeM: number | null;
  /** 북 0°, 시계 */
  headingDeg: number;
  /** 진행 방향 기울기 — 앞이 올라가면 +. 높이를 모르면 0 */
  pitchDeg: number;
}

/** 곡선 거리 `distanceM` 자리의 표본 높이(구간 비율 보간) — 모르면 null */
function altitudeAlongRoute(
  samples: readonly RouteSample[],
  cumulative: readonly number[],
  distanceM: number
): number | null {
  let index = 0;
  while (index < samples.length - 2 && cumulative[index + 1] < distanceM)
    index += 1;
  const [, , startAltitude] = samples[index];
  const [, , endAltitude] = samples[index + 1];
  if (startAltitude === null || endAltitude === null) return null;
  const segmentM = cumulative[index + 1] - cumulative[index];
  const t = segmentM === 0 ? 0 : (distanceM - cumulative[index]) / segmentM;
  return startAltitude + (endAltitude - startAltitude) * Math.min(Math.max(t, 0), 1);
}

/**
 * 경사 위의 자세 — 앞뒤 끝(지금 자리 ± `halfLengthM`, 동선 안으로 묶는다)이 동선 면에 닿게 기울인다. 바닥 중심 높이는
 * 앞뒤 끝을 잇는 바닥선 위 높이와 그 자리 동선 높이 중 높은 쪽(언덕 마루에서 몸통이 묻히지 않는다). 높이를 모르면 null.
 */
function slopeAlongRoute(
  samples: readonly RouteSample[],
  cumulative: readonly number[],
  distanceM: number,
  halfLengthM: number
): { altitudeM: number; pitchDeg: number } | null {
  const totalM = cumulative[cumulative.length - 1];
  const rearM = Math.max(distanceM - halfLengthM, 0);
  const frontM = Math.min(distanceM + halfLengthM, totalM);
  const here = altitudeAlongRoute(samples, cumulative, distanceM);
  const rear = altitudeAlongRoute(samples, cumulative, rearM);
  const front = altitudeAlongRoute(samples, cumulative, frontM);
  if (here === null || rear === null || front === null) return null;
  const spanM = frontM - rearM;
  if (spanM <= 0) return { altitudeM: here, pitchDeg: 0 };
  const onBase = rear + ((front - rear) * (distanceM - rearM)) / spanM;
  return {
    altitudeM: Math.max(onBase, here),
    pitchDeg: Math.atan2(front - rear, spanM) * RAD_TO_DEG,
  };
}

// 보간 폭은 양쪽 구간 절반으로 제한해 이웃 꼭짓점의 보간 구간과 겹치지 않는다
function turnBlendedHeadingDeg(
  samples: readonly RouteSample[],
  cumulative: readonly number[],
  vertexIndex: number,
  distanceM: number,
  turnBlendM: number
): number | null {
  if (vertexIndex <= 0 || vertexIndex >= samples.length - 1) return null;
  const beforeM = cumulative[vertexIndex] - cumulative[vertexIndex - 1];
  const afterM = cumulative[vertexIndex + 1] - cumulative[vertexIndex];
  const blendM = Math.min(turnBlendM, beforeM / 2, afterM / 2);
  const offsetM = distanceM - cumulative[vertexIndex];
  if (blendM <= 0 || Math.abs(offsetM) >= blendM) return null;
  return lerpAngleDeg(
    bearingDeg(lngLatOf(samples[vertexIndex - 1]), lngLatOf(samples[vertexIndex])),
    bearingDeg(lngLatOf(samples[vertexIndex]), lngLatOf(samples[vertexIndex + 1])),
    (offsetM + blendM) / (2 * blendM)
  );
}

/**
 * 표본 폴리라인 위 `distanceM` 지점의 자세 — 누적 거리로 구간을 찾아 위치는 선형 보간한다(v1 poseAlongPath). 거리는
 * 0 ~ 전체 길이로 묶는다(되감지 않는다). 길이가 0 이면 null.
 * `turnBlendM` 은 두 역할을 함께 정한다 — 꼭짓점 앞뒤 이 폭 안에서 방향을 앞 구간 → 뒤 구간으로 보간하는 꺾임 보간 폭이자,
 * 경사 자세(`altitudeM`·`pitchDeg`)를 잴 앞뒤 끝까지의 반폭(`slopeAlongRoute` 의 `halfLengthM`)이다.
 * 0 이면 꺾임 보간도 경사도 없다 — `pitchDeg` 는 늘 0, `altitudeM` 은 그 자리 동선 높이(모르면 null).
 */
export function poseAlongRoute(
  samples: readonly RouteSample[],
  cumulative: readonly number[],
  distanceM: number,
  turnBlendM: number
): RoutePose | null {
  if (samples.length < ROUTE_MIN_POINTS) return null;
  const totalM = cumulative[cumulative.length - 1];
  if (totalM === 0) return null;
  const clampedM = Math.min(Math.max(distanceM, 0), totalM);
  let segmentIndex = 0;
  while (
    segmentIndex < samples.length - 2 &&
    cumulative[segmentIndex + 1] <= clampedM
  ) {
    segmentIndex += 1;
  }
  // 길이 0 구간(겹친 표본)은 방향을 정할 수 없어 다음 구간으로 넘긴다
  while (
    segmentIndex < samples.length - 2 &&
    cumulative[segmentIndex + 1] === cumulative[segmentIndex]
  ) {
    segmentIndex += 1;
  }
  const start = samples[segmentIndex];
  const end = samples[segmentIndex + 1];
  const segmentM = cumulative[segmentIndex + 1] - cumulative[segmentIndex];
  const t =
    segmentM === 0 ? 0 : (clampedM - cumulative[segmentIndex]) / segmentM;
  const headingDeg =
    turnBlendedHeadingDeg(samples, cumulative, segmentIndex, clampedM, turnBlendM) ??
    turnBlendedHeadingDeg(
      samples,
      cumulative,
      segmentIndex + 1,
      clampedM,
      turnBlendM
    ) ??
    bearingDeg(lngLatOf(start), lngLatOf(end));
  const slope = slopeAlongRoute(samples, cumulative, clampedM, turnBlendM);
  return {
    lng: start[0] + (end[0] - start[0]) * t,
    lat: start[1] + (end[1] - start[1]) * t,
    altitudeM: slope?.altitudeM ?? null,
    headingDeg,
    pitchDeg: slope?.pitchDeg ?? 0,
  };
}

/** 방위(북 0°·시계)로 `distanceM` 만큼 간 자리 */
export const offsetByBearing = (
  origin: LngLat,
  bearing: number,
  distanceM: number
) =>
  offsetLngLat(origin, [
    Math.sin(bearing * DEG_TO_RAD) * distanceM,
    Math.cos(bearing * DEG_TO_RAD) * distanceM,
  ]);
